import React, { useContext, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { HomePageContext } from '../context/HomePageContext';
import PriceChart from '../components/PriceChart';
import ProductRatingStars from '../components/ProductRatingStars';
import './HomePage.css';

// Mapping für alte Phasenbezeichnungen auf neue
const normalizePhase = (phase) => {
  const mapping = {
    'Wachstumsphase (Veg)': 'Wachstum',
    'Blütephase (Flower)': 'Blüte'
  };
  return mapping[phase] || phase;
};

function HomePage() {
  const context = useContext(HomePageContext);
  
  if (!context) {
    throw new Error('HomePage muss innerhalb von HomePageProvider verwendet werden');
  }

  const {
    products,
    setProducts,
    manufacturers,
    setManufacturers,
    productTypes,
    setProductTypes,
    loading,
    setLoading,
    error,
    setError,
    viewMode,
    setViewMode,
    listSortBy,
    setListSortBy,
    listSortOrder,
    setListSortOrder,
    searchTerm,
    setSearchTerm,
    selectedManufacturer,
    setSelectedManufacturer,
    selectedProductType,
    setSelectedProductType,
    expandedManufacturer,
    setExpandedManufacturer
  } = context;

  // Nur beim ersten Laden oder wenn Daten leer sind, fetchen
  useEffect(() => {
    if (products.length === 0 && manufacturers.length === 0) {
      fetchAllData();
    }
  }, []);

  const fetchAllData = async () => {
    try {
      setLoading(true);
      
      // OPTIMIERT: Nur 3 API-Calls statt N+1
      // Neuer Endpoint liefert alle Produkte mit ihren Preisen bereits
      const [productsRes, mfgRes, typesRes] = await Promise.all([
        fetch('/api/products/with-prices/all?onlineOnly=true'), // ← NEU: Alle Daten in einem Call
        fetch('/api/manufacturers'),
        fetch('/api/product-types')
      ]);

      const productsWithPrices = await productsRes.json();
      const mfgData = await mfgRes.json();
      const typesData = await typesRes.json();

      setManufacturers(Array.isArray(mfgData) ? mfgData : []);
      setProductTypes(Array.isArray(typesData) ? typesData : []);

      // Safety check: API might return error object instead of array
      if (!Array.isArray(productsWithPrices)) {
        console.error('API returned non-array:', productsWithPrices);
        setProducts([]);
        setError(productsWithPrices?.message || 'Fehler beim Laden der Produkte');
        setLoading(false);
        return;
      }

      if (!productsWithPrices || productsWithPrices.length === 0) {
        setProducts([]);
        setLoading(false);
        return;
      }

      // Sortiere: Mit Preisen zuerst (nach Preis), dann ohne
      const withPrices = productsWithPrices.filter(p => p.bestPrice !== null);
      const withoutPrices = productsWithPrices.filter(p => p.bestPrice === null);
      
      withPrices.sort((a, b) => a.bestPrice - b.bestPrice);
      
      const sortedProducts = [...withPrices, ...withoutPrices];
      
      setProducts(sortedProducts);
      setError('');
      setLoading(false);
    } catch (err) {
      console.error('Fehler beim Laden der Daten:', err);
      setError('Fehler beim Laden der Daten');
      setLoading(false);
    }
  };

  // Gruppiere Produkte nach vordefinierten Kategorien
  // Diese werden jetzt aus dem Context bezogen, nicht als lokale State

  // Filtere Hersteller nach ausgewähltem Produkttyp
  const getFilteredManufacturers = () => {
    if (!selectedProductType) {
      return manufacturers;
    }
    
    // Filtere Hersteller, die den ausgewählten Produkttyp haben
    return manufacturers.filter(mfg => {
      if (!mfg.productTypeIds || mfg.productTypeIds.length === 0) {
        return false;
      }
      return mfg.productTypeIds.some(ptId => 
        typeof ptId === 'string' ? ptId === selectedProductType : ptId._id === selectedProductType
      );
    });
  };

  const getProductTypeName = (product) => {
    const categoryType = product.categories?.find(c => c.type === 'Produkttyp');
    if (categoryType?.name) {
      return categoryType.name;
    }

    const type = productTypes.find(t => t._id === product.productTypeId);
    return type?.name || '';
  };

  const getManufacturerName = (product) => {
    const manufacturer = manufacturers.find(m => m._id === product.manufacturerId);
    return manufacturer?.name || product.manufacturer?.name || '';
  };

  const getFilteredProducts = () => {
    let filtered = products;

    if (selectedProductType) {
      filtered = filtered.filter(p => p.productTypeId === selectedProductType);
    }

    if (selectedManufacturer) {
      filtered = filtered.filter(p => p.manufacturerId === selectedManufacturer);
    }

    // Filter nach Suchbegriff
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(p => {
        const matchesName = p.name?.toLowerCase().includes(searchLower);
        const manufacturer = manufacturers.find(m => m._id === p.manufacturerId);
        const matchesManufacturer = manufacturer?.name?.toLowerCase().includes(searchLower);
        const matchesType = getProductTypeName(p)?.toLowerCase().includes(searchLower);
        const matchesWatts = p.watts?.toString().includes(searchTerm);
        
        return matchesName || matchesManufacturer || matchesType || matchesWatts;
      });
    }

    return filtered;
  };

  const handleListSort = (column) => {
    if (listSortBy === column) {
      setListSortOrder(listSortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setListSortBy(column);
      setListSortOrder('asc');
    }
  };

  const renderListSortIcon = (column) => {
    if (listSortBy !== column) return ' ⇅';
    return listSortOrder === 'asc' ? ' ↑' : ' ↓';
  };

  const getSortedListProducts = () => {
    const filtered = getFilteredProducts();

    return [...filtered].sort((a, b) => {
      let aVal;
      let bVal;

      switch (listSortBy) {
        case 'name':
          aVal = a.name || '';
          bVal = b.name || '';
          break;
        case 'manufacturer':
          aVal = getManufacturerName(a);
          bVal = getManufacturerName(b);
          break;
        case 'type':
          aVal = getProductTypeName(a);
          bVal = getProductTypeName(b);
          break;
        case 'watts':
          aVal = a.watts ?? null;
          bVal = b.watts ?? null;
          break;
        case 'price':
        default:
          aVal = a.bestPrice ?? null;
          bVal = b.bestPrice ?? null;
          break;
      }

      const isString = typeof aVal === 'string' || typeof bVal === 'string';

      if (isString) {
        const aText = (aVal || '').toString().toLowerCase();
        const bText = (bVal || '').toString().toLowerCase();
        return listSortOrder === 'asc' ? aText.localeCompare(bText) : bText.localeCompare(aText);
      }

      if (aVal === null && bVal === null) return 0;
      if (aVal === null) return 1;
      if (bVal === null) return -1;

      return listSortOrder === 'asc' ? aVal - bVal : bVal - aVal;
    });
  };

  const getGroupedSections = () => {
    const sections = [];
    const filteredProducts = getFilteredProducts();

    // Wenn ein Filter aktiv ist, zeige alle passenden Hersteller dynamisch
    if (selectedProductType || selectedManufacturer) {
      const filteredManufacturers = selectedManufacturer
        ? manufacturers.filter(m => m._id === selectedManufacturer)
        : getFilteredManufacturers();

      filteredManufacturers.forEach(mfg => {
        const categoryProducts = filteredProducts.filter(p => {
          if (selectedProductType && p.productTypeId !== selectedProductType) {
            return false;
          }
          if (selectedManufacturer && p.manufacturerId !== selectedManufacturer) {
            return false;
          }
          return p.manufacturerId === mfg._id;
        });

        if (categoryProducts.length > 0) {
          sections.push({
            title: `${mfg.name}`,
            type: 'manufacturer',
            manufacturerId: mfg._id,
            products: categoryProducts,
            totalCount: categoryProducts.length,
            shouldShowExpandButton: false
          });
        }
      });

      return sections;
    }

    // Nur Hersteller-Kategorien - Dynamisch von allen Herstellern generiert
    const categoryConfig = manufacturers.map(mfg => ({
      name: mfg.name,
      type: 'manufacturer',
      id: mfg._id
    }));

    categoryConfig.forEach(config => {
      let categoryProducts = [];
      let mfg = null;

      if (config.type === 'manufacturer') {
        mfg = manufacturers.find(m => m._id === config.id);
        if (mfg) {
          // Filtere nach Hersteller wenn einer ausgewählt ist
          categoryProducts = filteredProducts.filter(p => {
            // Wenn ein Produkttyp ausgewählt ist, zeige nur Produkte dieses Typs
            if (selectedProductType && p.productTypeId !== selectedProductType) {
              return false;
            }
            // Wenn ein Hersteller ausgewählt ist, zeige nur Produkte dieses Herstellers
            if (selectedManufacturer && p.manufacturerId !== selectedManufacturer) {
              return false;
            }
            return p.manufacturerId === mfg._id;
          });
        }
      }

      if (categoryProducts.length > 0) {
        // Wenn kein Filter aktiv, zeige nur 4 (mit "Alle anzeigen" Button)
        // Wenn Filter aktiv, zeige alle
        const isFiltered = selectedManufacturer || selectedProductType || searchTerm;
        const displayProducts = !isFiltered && !expandedManufacturer ? categoryProducts.slice(0, 4) : categoryProducts;
        const shouldShowExpandButton = !isFiltered && categoryProducts.length > 4 && !expandedManufacturer;

        sections.push({
          title: `${config.name}`,
          type: config.type,
          manufacturerId: mfg?._id,
          products: displayProducts,
          totalCount: categoryProducts.length,
          shouldShowExpandButton: shouldShowExpandButton
        });
      }
    });

    return sections;
  };

  const sortedListProducts = getSortedListProducts();

  if (loading) {
    return (
      <div className="home-page">
        <div className="hero">
          <h1>🚀 Willkommen bei dichtlicht.de</h1>
          <p>Die beste Vergleichsseite für Grow-Zubehör</p>
        </div>
        <div className="container">
          <div className="suche-loading">⏳ Lädt Produkte...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="home-page">
        <div className="hero">
          <h1>🚀 Willkommen bei dichtlicht.de</h1>
          <p>Die beste Vergleichsseite für Grow-Zubehör</p>
        </div>
        <div className="container">
          <div className="suche-error">{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="home-page">
      <div className="hero">
        <h1>🚀 Willkommen bei dichtlicht.de</h1>
        <p>Die beste Vergleichsseite für Grow-Zubehör</p>
      </div>
      
      {/* Mobile Filter Dropdowns - nur auf Mobile sichtbar */}
      <div className="mobile-filter-dropdowns">
        <select 
          className="mobile-filter-select"
          value={selectedProductType || ''}
          onChange={(e) => {
            setSelectedProductType(e.target.value || null);
            setSelectedManufacturer(null);
            setExpandedManufacturer(null);
          }}
        >
          <option value="">📂 Alle Produkttypen</option>
          {productTypes.map(type => (
            <option key={type._id} value={type._id}>{type.name}</option>
          ))}
        </select>
        
        <select 
          className="mobile-filter-select"
          value={selectedManufacturer || ''}
          onChange={(e) => {
            setSelectedManufacturer(e.target.value || null);
            setExpandedManufacturer(null);
          }}
        >
          <option value="">🏭 Alle Hersteller</option>
          {getFilteredManufacturers().map(mfg => (
            <option key={mfg._id} value={mfg._id}>{mfg.name}</option>
          ))}
        </select>
      </div>
      
      <div className="home-content-wrapper">
        {/* Sidebar mit Produkttypen Filter und Herstellern */}
        <aside className="manufacturers-sidebar">
          <div className="sidebar-section">
            <h3>📂 Produkttypen</h3>
            <div className="product-type-list">
              <button 
                className={`filter-item ${!selectedProductType ? 'active' : ''}`}
                onClick={() => {
                  setSelectedProductType(null);
                  setSelectedManufacturer(null);
                  setExpandedManufacturer(null);
                }}
              >
                Alle Typen
              </button>
              {productTypes.map(type => (
                <button
                  key={type._id}
                  className={`filter-item ${selectedProductType === type._id ? 'active' : ''}`}
                  onClick={() => {
                    setSelectedProductType(type._id);
                    setSelectedManufacturer(null);
                  }}
                >
                  {type.name}
                </button>
              ))}
            </div>
          </div>

          <div className="sidebar-section">
            <h3>🏭 Hersteller</h3>
            <div className="manufacturer-list">
              <button 
                className={`filter-item ${!selectedManufacturer ? 'active' : ''}`}
                onClick={() => setSelectedManufacturer(null)}
              >
                Alle Hersteller
              </button>
              {getFilteredManufacturers().map(mfg => (
                <button
                  key={mfg._id}
                  className={`filter-item ${selectedManufacturer === mfg._id ? 'active' : ''}`}
                  onClick={() => {
                    setSelectedManufacturer(mfg._id);
                    setExpandedManufacturer(null);
                  }}
                >
                  {mfg.name}
                </button>
              ))}
            </div>
          </div>
        </aside>

        {/* Main Content */}
        <div className="container">
        <div className="view-toggle-bar">
          <div className="view-toggle">
            <button
              type="button"
              className={`view-toggle-button ${viewMode === 'grid' ? 'active' : ''}`}
              onClick={() => setViewMode('grid')}
            >
              Kacheln
            </button>
            <button
              type="button"
              className={`view-toggle-button ${viewMode === 'list' ? 'active' : ''}`}
              onClick={() => setViewMode('list')}
            >
              Liste
            </button>
          </div>
          {viewMode === 'list' && (
            <div className="list-sort-hint">Sortieren durch Klick auf Spalten</div>
          )}
          <div className="search-container">
            <span className="search-icon">🔍</span>
            <input
              type="search"
              className="search-input"
              placeholder="Produkt suchen..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              aria-label="Produkte suchen"
            />
            {searchTerm && (
              <button
                className="search-clear-btn"
                onClick={() => setSearchTerm('')}
                title="Suche löschen"
                aria-label="Suche löschen"
              >
                ✕
              </button>
            )}
          </div>
        </div>
        {viewMode === 'grid' ? (
          products.length === 0 ? (
            <div className="no-products">
              <p>Noch keine Produkte mit Preisen vorhanden.</p>
            </div>
          ) : (
            <>
              {getGroupedSections().map((section) => (
                <div key={`${section.type}-${section.manufacturerId || section.productTypeId}`}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                    <h2 className="category-header" style={{ margin: 0 }}>{section.title}</h2>
                    {section.shouldShowExpandButton && (
                      <button
                        onClick={() => setExpandedManufacturer(section.manufacturerId)}
                        style={{
                          padding: '0.5rem 1rem',
                          background: 'linear-gradient(135deg, rgba(0, 255, 136, 0.2) 0%, rgba(0, 200, 100, 0.15) 100%)',
                          border: '1px solid rgba(0, 255, 136, 0.4)',
                          color: '#00ff88',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '0.9rem',
                          fontWeight: '500',
                          transition: 'all 0.3s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.background = 'linear-gradient(135deg, rgba(0, 255, 136, 0.3) 0%, rgba(0, 200, 100, 0.25) 100%)';
                          e.target.style.borderColor = 'rgba(0, 255, 136, 0.6)';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.background = 'linear-gradient(135deg, rgba(0, 255, 136, 0.2) 0%, rgba(0, 200, 100, 0.15) 100%)';
                          e.target.style.borderColor = 'rgba(0, 255, 136, 0.4)';
                        }}
                      >
                        → Alle {section.totalCount} Produkte anzeigen
                      </button>
                    )}
                  </div>
                  <div className="products-grid">
                    {section.products.map((product) => {
                      const productTypeName = getProductTypeName(product).toLowerCase();
                      const isFertilizer = productTypeName.includes('dünger');
                      const isLighting = productTypeName.includes('beleuchtung');

                      return (
                      <div key={product._id} className={`product-card-wrapper ${isLighting && !product.dimmerIncluded ? 'missing-dimmer-warning' : ''}`}>
                        <Link 
                          to={`/product/${product._id}/suche`} 
                          className="product-card-link"
                        >
                          <div style={{ display: 'flex', gap: '0.75rem', padding: '0.75rem', minHeight: '280px', backgroundColor: 'rgba(10,10,20,0.8)', border: '1px solid rgba(0,255,136,0.2)', borderRadius: '8px', alignItems: 'stretch' }}>
                            {/* LINKE SPALTE: Bild mit Preis-Overlay */}
                            <div style={{ width: '30%', minWidth: '160px', display: 'flex', flexDirection: 'column', boxSizing: 'border-box' }}>
                              {/* Bild Container mit Preis-Overlay und Bewertungs-Sternen */}
                              <div style={{ position: 'relative', width: '100%', height: '100%', backgroundColor: 'rgba(255,255,255,0.95)', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '6px' }}>
                                {product.productImage ? (
                                  <img src={product.productImage} alt={product.name} style={{ width: '100%', height: 'auto', maxHeight: '100%', objectFit: 'contain' }} />
                                ) : (
                                  <div style={{ color: '#666', fontSize: '0.9rem' }}>Kein Bild</div>
                                )}
                                {/* Preis-Overlay oben rechts */}
                                {product.bestPrice && (
                                  <div style={{ position: 'absolute', top: '8px', right: '8px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0', padding: '0.4rem 0.6rem', background: 'linear-gradient(135deg, rgba(0, 255, 136, 0.95) 0%, rgba(0, 200, 100, 0.9) 100%)', borderRadius: '6px', boxShadow: '0 2px 8px rgba(0,0,0,0.3)' }}>
                                    <span style={{ color: 'rgba(0,0,0,0.6)', fontSize: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: '600' }}>ab</span>
                                    <span style={{ color: '#000', fontSize: '1rem', fontWeight: 'bold', lineHeight: '1' }}>€{product.bestPrice.toFixed(2)}</span>
                                  </div>
                                )}
                                {/* Bewertungs-Sterne unten mittig */}
                                <ProductRatingStars 
                                  productId={product._id}
                                />
                              </div>
                            </div>
                            {/* RECHTE SPALTE: Name, Specs, Features, Chart - volle Höhe */}
                            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', boxSizing: 'border-box', overflow: 'hidden', backgroundColor: 'rgba(10,10,20,0.8)', borderRadius: '6px', padding: '0.5rem' }}>
                              {/* Oben: Name, Specs, Features */}
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                {/* Name */}
                                <h3 style={{ margin: '0', fontSize: '1.2rem', fontWeight: '600', color: '#e0e0e0' }}>{product.name}</h3>
                                {/* Specs Badges */}
                                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                                  {product.watts && (
                                    <span style={{ background: 'rgba(0,255,136,0.15)', border: '1px solid rgba(0,255,136,0.3)', color: '#00ff88', padding: '0.25rem 0.5rem', fontSize: '0.65rem', borderRadius: '4px' }}>⚡ {product.watts}W</span>
                                  )}
                                {product.umolPerSecond && (
                                  <span style={{ background: 'rgba(0,255,136,0.15)', border: '1px solid rgba(0,255,136,0.3)', color: '#00ff88', padding: '0.25rem 0.5rem', fontSize: '0.65rem', borderRadius: '4px' }}>☀️ {product.umolPerSecond} mmol/s</span>
                                )}
                                {product.efficiency && (
                                  <span style={{ background: 'rgba(0,255,136,0.15)', border: '1px solid rgba(0,255,136,0.3)', color: '#00ff88', padding: '0.25rem 0.5rem', fontSize: '0.65rem', borderRadius: '4px' }}>💡 {product.efficiency} mmol/j</span>
                                )}
                                {product.growTentSize && (
                                  <span style={{ background: 'rgba(0,255,136,0.15)', border: '1px solid rgba(0,255,136,0.3)', color: '#00ff88', padding: '0.25rem 0.5rem', fontSize: '0.65rem', borderRadius: '4px' }}>🌱 {product.growTentSize}</span>
                                )}
                              </div>
                              {/* Features */}
                              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                                {isFertilizer ? (
                                  <>
                                    {product.fertilizerPhaseSuitability?.length > 0 && (
                                      <span style={{ background: 'rgba(0,212,255,0.15)', border: '1px solid rgba(0,212,255,0.3)', color: '#00d4ff', padding: '0.25rem 0.5rem', fontSize: '0.65rem', borderRadius: '4px' }}>
                                        🌱 Phasen: {product.fertilizerPhaseSuitability.map(normalizePhase).join(', ')}
                                      </span>
                                    )}
                                    {product.fertilizerSystemCompatibility?.length > 0 && (
                                      <span style={{ background: 'rgba(0,212,255,0.15)', border: '1px solid rgba(0,212,255,0.3)', color: '#00d4ff', padding: '0.25rem 0.5rem', fontSize: '0.65rem', borderRadius: '4px' }}>
                                        🧪 Systeme: {product.fertilizerSystemCompatibility.join(', ')}
                                      </span>
                                    )}
                                  </>
                                ) : (
                                  <>
                                    {product.smartControl && product.smartControl !== '' && (
                                      <span style={{ background: 'rgba(0,212,255,0.15)', border: '1px solid rgba(0,212,255,0.3)', color: '#00d4ff', padding: '0.25rem 0.5rem', fontSize: '0.65rem', borderRadius: '4px' }}>🎛️ {product.smartControl}</span>
                                    )}
                                    {product.dimmable && product.dimmable !== 'nicht dimmbar' && (
                                      <span style={{ background: 'rgba(0,212,255,0.15)', border: '1px solid rgba(0,212,255,0.3)', color: '#00d4ff', padding: '0.25rem 0.5rem', fontSize: '0.65rem', borderRadius: '4px' }}>🔦 {product.dimmable}</span>
                                    )}
                                    {isLighting && !product.dimmerIncluded && (
                                      <span style={{ background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.3)', color: '#ef4444', padding: '0.25rem 0.5rem', fontSize: '0.65rem', borderRadius: '4px' }}>⚠️ Kein Dimmer</span>
                                    )}
                                  </>
                                )}
                              </div>
                            </div>
                            {/* Chart - füllt Raum bis zu den Specs */}
                            <div className="product-card-chart-inline" style={{ marginTop: '0.5rem', flex: 1, minHeight: '80px', height: '80px' }}>
                              <PriceChart productId={product._id} />
                            </div>
                          </div>
                        </div>
                      </Link>
                    </div>
                  );
                  })}
                </div>
              </div>
            ))}
          </>
        )
        ) : (
          <div className="products-list-table-wrapper">
            {sortedListProducts.length === 0 ? (
              <div className="no-products">
                <p>Noch keine Produkte mit Preisen vorhanden.</p>
              </div>
            ) : (
              <table className="products-list-table">
                <thead>
                  <tr>
                    <th className="col-product sortable" onClick={() => handleListSort('name')}>
                      Produkt{renderListSortIcon('name')}
                    </th>
                    <th className="col-manufacturer sortable" onClick={() => handleListSort('manufacturer')}>
                      Hersteller{renderListSortIcon('manufacturer')}
                    </th>
                    <th className="col-type sortable" onClick={() => handleListSort('type')}>
                      Typ{renderListSortIcon('type')}
                    </th>
                    <th className="col-watts sortable" onClick={() => handleListSort('watts')}>
                      Watt{renderListSortIcon('watts')}
                    </th>
                    <th className="col-price sortable" onClick={() => handleListSort('price')}>
                      Preis{renderListSortIcon('price')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortedListProducts.map((product) => (
                    <tr key={product._id}>
                      <td className="col-product">
                        <Link to={`/product/${product._id}/suche`} className="product-list-link">
                          {product.productImage ? (
                            <img
                              src={product.productImage}
                              alt={product.name}
                              className="product-list-thumb"
                            />
                          ) : (
                            <span className="product-list-thumb placeholder">🧰</span>
                          )}
                          <span className="product-list-name">{product.name}</span>
                        </Link>
                      </td>
                      <td className="col-manufacturer">{getManufacturerName(product) || '—'}</td>
                      <td className="col-type">{getProductTypeName(product) || '—'}</td>
                      <td className="col-watts">{product.watts ? `${product.watts}W` : '—'}</td>
                      <td className="col-price">{product.bestPrice ? `€${product.bestPrice.toFixed(2)}` : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
        </div>
      </div>
    </div>
  );
}

export default HomePage;
